import React, { useEffect, useRef, useState } from 'react';
import { Clapperboard, Download, Loader2, Play, Volume2 } from 'lucide-react';

import {
  assistantsService,
  type Capsule,
  type Restitution,
  type Voix,
} from '../../services/api/assistants';

interface Props {
  restitution?: Restitution;
  capsule?: Capsule;
  conversationId: number;
  messageId: number;
}

/** Débit mesuré sur la voix de synthèse, en caractères par seconde. */
const CARACTERES_PAR_SECONDE = 17;

const duree = (texte: string) => Math.round(texte.length / CARACTERES_PAR_SECONDE) + 1;

const minutes = (secondes: number) => {
  const m = Math.floor(secondes / 60);
  const s = secondes % 60;
  return m ? `${m} min ${String(s).padStart(2, '0')}` : `${s} s`;
};

/**
 * Découpage d'une vidéo, puis la vidéo montée.
 *
 * Le montage n'est pas fait avec le reste de la réponse. Pour une restitution
 * c'est une affaire de temps — chaque plan attend sa synthèse vocale. Pour une
 * capsule c'est une affaire d'argent : chaque plan est facturé au fournisseur
 * vidéo, et un découpage se relit avant d'être tourné.
 */
const RenduVideo: React.FC<Props> = ({ restitution, capsule, conversationId, messageId }) => {
  const [voix, setVoix] = useState<Voix[]>([]);
  const [choix, setChoix] = useState('');
  const [video, setVideo] = useState<string | null>(null);
  const [travail, setTravail] = useState(false);
  const [erreur, setErreur] = useState('');
  const [avancement, setAvancement] = useState('');
  const [ecoule, setEcoule] = useState(0);
  const [attente, setAttente] = useState<number | null>(null);
  const [lue, setLue] = useState<number | null>(null);
  const precedente = useRef<string | null>(null);
  const suivi = useRef<ReturnType<typeof setInterval> | null>(null);
  const lecteur = useRef<HTMLAudioElement | null>(null);
  const sonScene = useRef<string | null>(null);

  /**
   * Fait dire une scène par la voix retenue.
   *
   * Le haut-parleur n'était qu'une icône dans un paragraphe : les équipes du
   * client ont cliqué dessus et conclu que la lecture ne fonctionnait pas.
   * Elle lit désormais, avec la voix qui sera celle du montage. Entendre une
   * narration avant de lancer les plans est précisément ce qui évite de payer
   * un montage pour s'apercevoir ensuite que le texte sonne faux.
   */
  const ecouterScene = async (rang: number, texte: string) => {
    if (!texte.trim() || attente !== null) return;
    setErreur('');
    setAttente(rang);
    try {
      const son = await assistantsService.synthetiser(texte, choix || undefined);
      if (sonScene.current) URL.revokeObjectURL(sonScene.current);
      sonScene.current = URL.createObjectURL(son);

      const machine = lecteur.current || new Audio();
      lecteur.current = machine;
      machine.src = sonScene.current;
      machine.onended = () => setLue(null);
      await machine.play();
      setLue(rang);
    } catch (e: any) {
      setErreur(e?.message || "La narration n'a pas pu être lue.");
    } finally {
      setAttente(null);
    }
  };

  useEffect(() => {
    assistantsService.listerVoix()
      .then((r) => setVoix(r.voix))
      .catch(() => undefined);

    // Un montage lancé puis quitté continue côté serveur. En revenant, on se
    // rattache à lui plutôt que d'afficher un écran vierge : sans cela, il
    // fallait recliquer sur « Produire » pour retrouver l'avancement, au
    // risque d'en déclencher un second.
    assistantsService.montageEnCours(conversationId, messageId)
      .then((tache) => {
        if (!tache) return;
        setTravail(true);
        setAvancement(tache.avancement);
        setEcoule(tache.duree_secondes);
        suivre(tache.id);
      })
      .catch(() => undefined);

    return () => {
      if (precedente.current) URL.revokeObjectURL(precedente.current);
      if (sonScene.current) URL.revokeObjectURL(sonScene.current);
      lecteur.current?.pause();
      // Le montage continue côté serveur ; seul le suivi s'arrête.
      if (suivi.current) clearInterval(suivi.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, messageId]);

  const titre = restitution?.titre || capsule?.titre || 'Vidéo';
  const narrations = restitution
    ? (restitution.slides || []).map((s) => s.notes)
    : (capsule?.plans || []).map((p) => p.narration);
  const estimation = narrations.reduce((total, texte) => total + duree(texte), 0)
    + (restitution ? 4 : 0);

  // Le montage tourne côté serveur : on dépose le travail, puis on suit son
  // avancement. Attendre dans la requête faisait échouer les capsules en 502
  // au bout de quatre minutes.
  const monter = async () => {
    setTravail(true);
    setErreur('');
    setAvancement('');
    try {
      const tache = await assistantsService.demanderVideo(
        conversationId, messageId, choix,
      );
      suivre(tache.id);
    } catch (e: any) {
      setErreur(e.message || "Le montage n'a pas pu être lancé.");
      setTravail(false);
    }
  };

  const suivre = (tacheId: number) => {
    // Le montage continue sur le serveur pendant qu'une requête de suivi rate.
    // Un seul raté coupait le suivi et affichait « Failed to fetch » : la
    // production paraissait perdue alors qu'elle aboutissait. Sur une
    // connexion de Conakry, c'est la règle plutôt que l'exception.
    let rates = 0;
    const RATES_TOLERES = 5;

    // Trois secondes : assez pour voir défiler les plans, assez peu pour ne
    // pas marteler le serveur pendant les longues minutes d'un tournage.
    const minuteur = setInterval(async () => {
      try {
        const etat = await assistantsService.suivreTache(tacheId);
        rates = 0;
        setAvancement(etat.avancement);
        setEcoule(etat.duree_secondes);
        if (etat.etat === 'terminee') {
          clearInterval(minuteur);
          const blob = await assistantsService.recupererProduction(tacheId);
          if (precedente.current) URL.revokeObjectURL(precedente.current);
          precedente.current = URL.createObjectURL(blob);
          setVideo(precedente.current);
          setTravail(false);
        } else if (etat.etat === 'echouee') {
          clearInterval(minuteur);
          setErreur(etat.erreur || "Le montage a échoué.");
          setTravail(false);
        }
      } catch (e: any) {
        rates += 1;
        if (rates <= RATES_TOLERES) {
          setAvancement('Connexion interrompue, reprise du suivi');
          return;
        }
        clearInterval(minuteur);
        // On ne dit pas que le montage a échoué : on ne le sait pas. On dit
        // qu'on l'a perdu de vue, et comment le retrouver.
        setErreur(
          "Le suivi du montage est interrompu : la connexion ne répond plus. "
          + "Le montage, lui, se poursuit sur le serveur. Rouvrez cette page "
          + "dans quelques minutes pour le récupérer, sans le relancer.",
        );
        setTravail(false);
      }
    }, 3000);
    suivi.current = minuteur;
  };

  const telecharger = () => {
    if (!video) return;
    const lien = document.createElement('a');
    lien.href = video;
    lien.download = `${titre.replace(/[\\/:*?"<>|]/g, '')}.mp4`;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 text-sm text-eg-gris">
          <Clapperboard className="h-4 w-4" />
          {restitution ? 'Restitution commentée' : `Capsule — ${capsule?.public}`}
        </div>
        <h2 className="mt-1 text-xl font-semibold text-eg-encre">{titre}</h2>
        {restitution?.sous_titre && (
          <p className="mt-1 text-[13px] text-eg-gris">{restitution.sous_titre}</p>
        )}
      </div>

      {/* Le découpage, plan par plan : c'est ce qu'on relit avant de monter. */}
      <ol className="space-y-2">
        {(restitution?.slides || capsule?.plans || []).map((plan: any, rang: number) => (
          <li
            key={rang}
            className="rounded-md border border-eg-bord bg-eg-blanc px-4 py-3"
          >
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-[13px] font-semibold text-eg-encre">
                {rang + 1}. {restitution ? plan.titre : `Plan ${rang + 1}`}
              </p>
              <span className="shrink-0 text-[11px] text-eg-estompe">
                {minutes(duree(restitution ? plan.notes : plan.narration))}
              </span>
            </div>

            {restitution ? (
              <ul className="mt-1.5 space-y-0.5 text-[12.5px] text-eg-encre">
                {plan.puces.map((puce: string, i: number) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-eg-accent">—</span> {puce}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-1.5 text-[12.5px] italic text-eg-gris">{plan.description}</p>
            )}

            <p className="mt-2 flex gap-2 border-t border-eg-bordure pt-2 text-[12.5px] text-eg-encre">
              <button
                type="button"
                onClick={() => ecouterScene(rang, restitution ? plan.notes : plan.narration)}
                disabled={attente !== null}
                title="Écouter cette narration avec la voix retenue"
                aria-label={`Écouter la narration de la scène ${rang + 1}`}
                className="mt-0.5 shrink-0 text-eg-gris transition-colors hover:text-eg-accent
                  disabled:opacity-40"
              >
                {attente === rang
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Volume2 className={`h-3.5 w-3.5 ${lue === rang ? 'text-eg-accent' : ''}`} />}
              </button>
              {restitution ? plan.notes : plan.narration}
            </p>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap items-center gap-2">
        <select
          value={choix}
          onChange={(e) => setChoix(e.target.value)}
          disabled={travail}
          className="rounded-md border border-[#DDE2DD] bg-eg-blanc px-2 py-1.5 text-[12.5px]
            focus:border-eg-accent focus:outline-none disabled:opacity-50"
        >
          <option value="">Voix par défaut</option>
          {voix.map((v) => (
            <option key={v.id} value={v.id}>{v.nom}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={monter}
          disabled={travail}
          className="inline-flex items-center gap-1.5 rounded-[5px] bg-eg-ombre px-3.5 py-1.5
            text-[12.5px] font-semibold text-eg-blanc transition-colors hover:bg-eg-encre
            disabled:opacity-40"
        >
          {travail ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Play className="h-3.5 w-3.5" />
          )}
          {video ? 'Refaire le montage' : 'Monter la vidéo'}
        </button>

        {video && (
          <button
            type="button"
            onClick={telecharger}
            className="inline-flex items-center gap-1.5 rounded-[5px] border border-eg-bord
              bg-eg-blanc px-3 py-1.5 text-[12.5px] font-semibold text-eg-brun
              transition-colors hover:bg-eg-papier"
          >
            <Download className="h-3.5 w-3.5" /> .mp4
          </button>
        )}

        <span className="text-[11px] text-eg-estompe">
          Durée estimée : {minutes(estimation)}
        </span>
      </div>

      {/* Une capsule engage une dépense chez le fournisseur : on le dit avant,
          pas après. */}
      {capsule && !video && (
        <p className="rounded-md border border-eg-alerte/30 bg-eg-alerte/10 px-3 py-2
          text-[12px] text-eg-alerte">
          {capsule.plans.length} plan{capsule.plans.length > 1 ? 's' : ''} à tourner sur le
          compte Google du programme. Comptez deux à trois minutes par plan, et relisez
          le découpage avant de lancer : chaque montage est facturé.
        </p>
      )}

      {travail && (
        <div className="rounded-md border border-eg-bord bg-eg-papier px-4 py-3">
          <p className="flex items-center gap-2 text-[12.5px] text-eg-encre">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {avancement || 'Mise en file du montage'}
            {ecoule > 0 && (
              <span className="text-eg-estompe">· {minutes(ecoule)}</span>
            )}
          </p>
          <p className="mt-1 text-[11px] text-eg-gris">
            Le montage se poursuit sur le serveur. Vous pouvez quitter cette page et
            revenir : il ne sera pas perdu.
          </p>
        </div>
      )}

      {erreur && (
        <p className="rounded-md border border-eg-accent/30 bg-eg-accent/10 px-3 py-2 text-sm
          text-eg-accent">{erreur}</p>
      )}

      {video && (
        <video
          src={video}
          controls
          className="w-full rounded-md border border-eg-bord bg-black"
        />
      )}

      {capsule && capsule.mentions_obligatoires?.length > 0 && (
        <div className="rounded-md border border-eg-bord bg-eg-papier px-4 py-3">
          <p className="mb-1 text-[10px] uppercase tracking-[0.04em] text-eg-gris">
            Mentions à faire figurer
          </p>
          <ul className="space-y-0.5 text-[12px] text-eg-encre">
            {capsule.mentions_obligatoires.map((mention, rang) => (
              <li key={rang}>{mention}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RenduVideo;
